import {
  type ConnectorIdentity,
  toConnectorIdentity,
} from "@p0security/integrations-one-password-shared/identity";
import {
  type TokenSecret,
  toTokenSecret,
} from "@p0security/integrations-one-password-shared/token-secret";
import type {
  OnePasswordAccountConfig,
  OnePasswordConnectorHosting,
} from "@p0security/integrations-one-password-shared/types";
import { tfId } from "@p0security/integrations-vaulted-credential-shared";

import { toHclComment } from "./hcl.ts";

type AwsIdentity = Extract<ConnectorIdentity, { type: "aws" }>;
type GcpIdentity = Extract<ConnectorIdentity, { type: "gcp" }>;
type AwsSecretManager = Extract<
  OnePasswordAccountConfig["secretManager"],
  { type: "aws-sm" }
>;

/** The arguments to {@link renderReadGrantTerraform}. */
export type ReadGrantArgs = {
  /** The install item id of the account's connector */
  connectorId: string;
  /** Where that connector runs */
  hosting: OnePasswordConnectorHosting;
  item: Pick<OnePasswordAccountConfig, "secretManager" | "tokenSecretName">;
  /** The 1Password account's sign-in address, such as acme.1password.com,
   * which is its install item's id */
  accountId: string;
};

/** The configuration for the secret's own AWS account, when it isn't the
 * connector's. */
type SecretAccountBlock = {
  purpose: "secret-account";
  accountId: string;
  terraform: string;
};

/** One Terraform configuration of an account's read grant, and where
 * the customer applies it. */
export type ReadGrantBlock =
  | SecretAccountBlock
  | { purpose: "grant"; terraform: string };

export const isSecretAccountBlock = (
  block: ReadGrantBlock
): block is SecretAccountBlock => block.purpose === "secret-account";

/** The Terraform name of an account's resources. A sign-in address has dots,
 * which a Terraform name can't, and one account's names mustn't clash with a
 * connector's in the same configuration. */
export const toAccountTfId = (accountId: string) =>
  tfId(`p0_1password_account_${accountId.replace(/[^A-Za-z0-9_-]/g, "_")}`);

/** The name of the inline policy that an account adds to its connector's
 * role, which holds one for each account the connector serves. IAM allows the
 * dots and hyphens of a sign-in address in a policy's name. */
// IAM caps an inline policy's name at 128 characters, and a sign-in address
// can be long.
const POLICY_NAME_MAX_LENGTH = 128;

const toPolicyName = (accountId: string) =>
  `Read1PasswordToken-${accountId}`.slice(0, POLICY_NAME_MAX_LENGTH);

/** Where an account's secret is, next to its connector on AWS. */
type AwsSecretPlacement = {
  /** Whether the secret is in another account than the connector */
  crossAccount: boolean;
  secretAccountId: string;
};

/** The comment on the setting for the KMS key's ARN, as `#` lines that each
 * line after the first starts with `indent`. */
const renderKmsKeyComment = ({
  crossAccount,
  indent,
  secretAccountId,
}: AwsSecretPlacement & {
  /** Starts each line after the first */
  indent: string;
}) =>
  toHclComment(
    crossAccount
      ? [
          "The ARN of the KMS key that encrypts the secret. The secret is in",
          `account ${secretAccountId}, so it needs a customer-managed key: the default`,
          "aws/secretsmanager key can't be used from another account. Set this to",
          `that key's ARN, and apply the configuration for account ${secretAccountId}`,
          "too, which lets the connector's role read the secret there.",
        ]
      : [
          "The ARN of the KMS key that encrypts the secret, if that's a",
          "customer-managed key. The default aws/secretsmanager key needs nothing",
          "for a secret in this account.",
        ],
    indent
  );

/** The policy's lifecycle: a secret in another account than the connector's
 * needs a customer-managed key, and the plan fails until it's set. */
const renderCrossAccountPrecondition = (id: string) => `

  lifecycle {
    precondition {
      condition     = local.${id}.kms_key_arn != null
      error_message = "The service account token's secret is in another AWS account, so it must be encrypted with a customer-managed KMS key. Set kms_key_arn to that key's ARN."
    }
  }`;

/**
 * Renders the Terraform that lets a connector on AWS read one account's
 * service account token: an inline policy on the connector's role, for the one secret
 * that P0 points the connector at.
 */
const renderAwsGrant = (
  { accountId, connectorId }: ReadGrantArgs,
  identity: AwsIdentity,
  secret: Extract<TokenSecret, { manager: "aws" }>,
  placement: AwsSecretPlacement
) => {
  const id = toAccountTfId(accountId);

  return `# Lets P0's 1Password connector ${connectorId} read the service account token
# of the 1Password account ${accountId}, and no other secret.
# Apply it in the connector's AWS account, ${identity.accountId}.

locals {
  ${id} = {
    # The connector's role, which the connector's own Terraform creates.
    connector_account_id = "${identity.accountId}"
    connector_role_name  = "${identity.roleName}"
    # The secret that holds the account's token, by its partial ARN: its ARN
    # without the hyphen and six random characters that end it. P0 gives the
    # connector this ARN.
    token_secret_partial_arn = "${secret.secretId}"
    secrets_region           = "${secret.region}"
    ${renderKmsKeyComment({ ...placement, indent: "    " })}
    kms_key_arn = null
  }
}

# IAM is global, so the region only has to be one you use. Add the credential
# settings your other AWS providers use, such as profile or assume_role, if
# they have any.
provider "aws" {
  alias               = "${id}"
  region              = local.${id}.secrets_region
  allowed_account_ids = [local.${id}.connector_account_id]
}

data "aws_partition" "${id}" {
  provider = aws.${id}
}

# The connector reads the account's service account token. Each account adds a
# policy of its own to the connector's role, and a role's inline policies share
# a limit of 10,240 characters: past a few dozen accounts, deploy another
# connector for the rest.
resource "aws_iam_role_policy" "${id}_token" {
  provider = aws.${id}

  name = "${toPolicyName(accountId)}"
  role = local.${id}.connector_role_name
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = concat(
      [{
        Sid    = "ReadServiceAccountToken"
        Effect = "Allow"
        Action = "secretsmanager:GetSecretValue"
        # "??????" matches the six random characters that end the secret's
        # ARN, so the connector gets this one secret and no other.
        Resource = "\${local.${id}.token_secret_partial_arn}-??????"
      }],
      local.${id}.kms_key_arn == null ? [] : [{
        Sid      = "DecryptServiceAccountToken"
        Effect   = "Allow"
        Action   = "kms:Decrypt"
        Resource = local.${id}.kms_key_arn
        Condition = {
          StringEquals = {
            "kms:ViaService" = "secretsmanager.\${local.${id}.secrets_region}.\${data.aws_partition.${id}.dns_suffix}"
          }
        }
      }],
    )
  })${placement.crossAccount ? renderCrossAccountPrecondition(id) : ""}
}
`;
};

/**
 * Renders the Terraform for the secret's own account, when the connector
 * runs in another: the secret's resource policy, and the statement that the
 * secret's KMS key policy needs.
 *
 * Both name the connector's role, so the role must exist first: AWS refuses
 * a policy whose principal it can't find.
 */
const renderAwsSecretAccount = (
  { accountId, connectorId, item }: ReadGrantArgs,
  identity: AwsIdentity,
  { install, secretsRegion }: AwsSecretManager
) => {
  const id = `${toAccountTfId(accountId)}_secret_account`;
  const secretAccountId = install.trim();

  return `# The service account token of the 1Password account ${accountId}
# is in AWS account ${secretAccountId}, and P0's 1Password connector ${connectorId} runs in
# account ${identity.accountId}. Apply this in account ${secretAccountId}, after the
# connector's own Terraform. It lets the connector's role read the secret there.
#
# WARNING: aws_secretsmanager_secret_policy replaces the secret's whole resource
# policy. If the secret already has one, add the statement below to it where
# you manage it, and leave that resource out.

locals {
  ${id} = {
    # Where the secret that holds the account's service account token is.
    secret_account_id = "${secretAccountId}"
    secrets_region    = "${secretsRegion.trim()}"
    token_secret_name = "${item.tokenSecretName.trim()}"
    # The connector's role, which the connector's own Terraform creates.
    connector_role_arn = "${identity.roleArn}"
  }
}

# Add the credential settings your other AWS providers use, such as profile or
# assume_role, if they have any.
provider "aws" {
  alias               = "${id}"
  region              = local.${id}.secrets_region
  allowed_account_ids = [local.${id}.secret_account_id]
}

data "aws_partition" "${id}" {
  provider = aws.${id}
}

data "aws_secretsmanager_secret" "${id}" {
  provider = aws.${id}

  name = local.${id}.token_secret_name
}

resource "aws_secretsmanager_secret_policy" "${id}" {
  provider = aws.${id}

  secret_arn = data.aws_secretsmanager_secret.${id}.arn
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Sid       = "P0OnePasswordConnectorReadsToken"
      Effect    = "Allow"
      Principal = { AWS = local.${id}.connector_role_arn }
      Action    = "secretsmanager:GetSecretValue"
      Resource  = "*"
    }]
  })
}

# The statement to add to the policy of the customer-managed KMS key that
# encrypts the secret. Terraform can't add one statement to a key's policy,
# since aws_kms_key_policy replaces the whole policy, so add it where you
# manage the key. The default aws/secretsmanager key can't be used from another
# account, so the secret needs a key of its own.
output "${id}_kms_key_policy_statement" {
  value = {
    Sid       = "P0OnePasswordConnectorDecryptsToken"
    Effect    = "Allow"
    Principal = { AWS = local.${id}.connector_role_arn }
    Action    = "kms:Decrypt"
    Resource  = "*"
    Condition = {
      StringEquals = {
        "kms:ViaService" = "secretsmanager.\${local.${id}.secrets_region}.\${data.aws_partition.${id}.dns_suffix}"
      }
    }
  }
}
`;
};

/**
 * Renders the Terraform that lets a connector on Cloud Run read one account's
 * service account token: two bindings on the secret itself, in the
 * secret's project, which may not be the connector's.
 *
 * The connector reads the latest version's value, then that version, so it
 * needs `secretmanager.versions.get` as well as the accessor role's
 * `secretmanager.versions.access`. Both roles are bound on the secret and
 * not on its project, so the connector can read no other secret.
 */
const renderGcpGrant = (
  { accountId, connectorId }: ReadGrantArgs,
  identity: GcpIdentity,
  secret: Extract<TokenSecret, { manager: "gcp" }>
) => {
  const id = toAccountTfId(accountId);

  return `# Lets P0's 1Password connector ${connectorId} read the service account token
# of the 1Password account ${accountId}, and no other secret.
# Apply it where you manage project ${secret.projectId}, which holds the
# secret. The secret must exist first.
#
# Each binding adds one member to the secret's IAM policy, and replaces
# nothing.

locals {
  ${id} = {
    # The secret that holds the account's service account token, and its project.
    token_secret_project = "${secret.projectId}"
    token_secret_id      = "${secret.secretId}"
    # The connector's service account, which the connector's own Terraform
    # creates.
    connector_service_account = "${identity.serviceAccountEmail}"
  }
}

# The connector reads the service account token's value.
resource "google_secret_manager_secret_iam_member" "${id}_token_accessor" {
  project   = local.${id}.token_secret_project
  secret_id = local.${id}.token_secret_id
  role      = "roles/secretmanager.secretAccessor"
  member    = "serviceAccount:\${local.${id}.connector_service_account}"
}

# The accessor role gives secretmanager.versions.access only. The connector also
# reads the version it got the value from, which needs
# secretmanager.versions.get.
resource "google_secret_manager_secret_iam_member" "${id}_token_viewer" {
  project   = local.${id}.token_secret_project
  secret_id = local.${id}.token_secret_id
  role      = "roles/secretmanager.viewer"
  member    = "serviceAccount:\${local.${id}.connector_service_account}"
}
`;
};

/**
 * Renders the Terraform that lets an account's connector read that account's
 * service account token, and nothing else, for the customer to apply
 * in their own cloud.
 *
 * On AWS, a secret in another account than the connector's takes a second
 * configuration, applied in the secret's account.
 *
 * @returns The configurations in the order to apply them, or `undefined` when
 * the secret isn't in the connector's cloud. P0 refuses to store such an
 * account, so only a hand-edited one can be.
 */
export const renderReadGrantTerraform = (
  args: ReadGrantArgs
): ReadGrantBlock[] | undefined => {
  const { hosting, item } = args;
  const { secretManager } = item;
  const identity = toConnectorIdentity(hosting);
  const secret = toTokenSecret(item);
  if (
    identity.type === "aws" &&
    secret.manager === "aws" &&
    secretManager.type === "aws-sm"
  ) {
    const secretAccountId = secretManager.install.trim();
    const crossAccount = secretAccountId !== identity.accountId;
    return [
      {
        purpose: "grant",
        terraform: renderAwsGrant(args, identity, secret, {
          crossAccount,
          secretAccountId,
        }),
      },
      ...(crossAccount
        ? [
            {
              accountId: secretAccountId,
              purpose: "secret-account" as const,
              terraform: renderAwsSecretAccount(args, identity, secretManager),
            },
          ]
        : []),
    ];
  }
  if (identity.type === "gcp" && secret.manager === "gcp") {
    return [
      { purpose: "grant", terraform: renderGcpGrant(args, identity, secret) },
    ];
  }
  return undefined;
};
